import React, { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { OrganizationDetailsQuery } from '@plug/graphql/queries/organization-details';
import { ORGANIZATION_DETAILS } from '@plug/domains/organization/graphql/queries/organization-details';
import { useLocation } from 'react-router-dom';
import { USER_LOCAL_STORAGE_KEY } from '@plug/config/consts';

import cookie from 'js-cookie';

const GLEAP_SDK_KEY = 'XcCEJgBTiJKFqOM4yTYBeJqc8741koTx';
const GLEAP_SCRIPT_ID = 'gleap-sdk-script';
const GLEAP_SCRIPT_URL = 'https://js.gleap.io/latest/index.js';

type GleapIdentifyData = {
  email?: string;
  name?: string;
  company?: {
    id?: string;
    name?: string;
  };
  customData?: {
    domain?: string;
    integration?: string;
  };
};

type GleapSDK = {
  initialize: (sdkKey: string) => void;
  identify: (userId: string, customerData: GleapIdentifyData) => void;
  destroy: () => void;
};

declare global {
  interface Window {
    Gleap?: GleapSDK;
  }
}

let gleapScriptPromise: Promise<GleapSDK> | null = null;

const loadGleapScript = (): Promise<GleapSDK> => {
  if (window.Gleap) {
    return Promise.resolve(window.Gleap);
  }

  if (gleapScriptPromise) {
    return gleapScriptPromise;
  }

  gleapScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GLEAP_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.Gleap) {
          resolve(window.Gleap);
          return;
        }

        reject(new Error('Gleap SDK loaded but window.Gleap is unavailable.'));
      });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Gleap SDK.')));
      return;
    }

    const script = document.createElement('script');
    script.id = GLEAP_SCRIPT_ID;
    script.src = GLEAP_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.Gleap) {
        resolve(window.Gleap);
        return;
      }

      reject(new Error('Gleap SDK loaded but window.Gleap is unavailable.'));
    };
    script.onerror = () => reject(new Error('Failed to load Gleap SDK.'));
    document.body.appendChild(script);
  });

  return gleapScriptPromise;
};

export const GleapComponent = () => {
  const { data: organizationDetailsData } = useQuery<OrganizationDetailsQuery>(
    ORGANIZATION_DETAILS,
  );
  const location = useLocation();
  const gleapInitializedRef = useRef<boolean>(false);
  const user = JSON.parse(decodeURIComponent(cookie.get(USER_LOCAL_STORAGE_KEY) || '{}'));

  useEffect(() => {
    if (!organizationDetailsData) return;

    const isAdmin =
      organizationDetailsData?.organizationDetails?.organizationRole[0]?.name === 'ADMIN';

    let isMounted = true;

    const syncGleap = async () => {
      if (isAdmin) {
        if (gleapInitializedRef.current) return;

        try {
          const Gleap = await loadGleapScript();
          if (!isMounted) return;

          Gleap.initialize(GLEAP_SDK_KEY);
          Gleap.identify(user?.id, {
            email: user?.email,
            name: user?.username,
            company: {
              id: organizationDetailsData?.organizationDetails?.id,
              name: organizationDetailsData?.organizationDetails?.name,
            },
            customData: {
              domain: organizationDetailsData?.organizationDetails?.domain,
              integration: organizationDetailsData?.organizationDetails?.integration?.type,
            },
          });
          gleapInitializedRef.current = true;
        } catch (error) {
          console.error('Failed to initialize Gleap SDK.', error);
        }

        return;
      }

      if (gleapInitializedRef.current && window.Gleap) {
        window.Gleap.destroy();
        gleapInitializedRef.current = false;
      }
    };

    syncGleap();

    return () => {
      isMounted = false;
    };
  }, [organizationDetailsData, location, user]);

  return <></>;
};
